import { useState, type ReactElement } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  fetchAiContentReports,
  type AiContentReport,
  type AiReportReason,
  type AiReportSurface,
} from '../../api/admin.js';
import { formatDateTime } from '../../lib/format.js';

const REASON_LABEL: Record<AiReportReason, string> = {
  offensive: 'Ofensiva o inapropiada',
  incorrect: 'Información incorrecta',
  other: 'Otro',
};

const SURFACE_LABEL: Record<AiReportSurface, string> = {
  conversation: 'Charla',
  session_history: 'Historial',
  theory_chat: 'Chat de teoría',
};

/**
 * AI replies learners flagged from speak (Google Play "AI-Generated Content"
 * policy). Read-only, newest first; the session link opens the full session.
 */
export function AiReportsPage(): ReactElement {
  const [reason, setReason] = useState<AiReportReason | ''>('');
  const [surface, setSurface] = useState<AiReportSurface | ''>('');
  const [cursor, setCursor] = useState<string | undefined>(undefined);

  const query = useQuery({
    queryKey: ['admin', 'ai-content-reports', reason, surface, cursor],
    queryFn: () =>
      fetchAiContentReports({
        reason: reason || undefined,
        surface: surface || undefined,
        cursor,
      }),
  });

  return (
    <>
      <div className="page-header">
        <div>
          <h1 className="page-title">Reportes de IA</h1>
          <p className="page-sub">Respuestas del coach que los alumnos marcaron desde speak</p>
        </div>
        <div className="filters">
          <select
            value={reason}
            onChange={(e) => {
              setReason(e.target.value as AiReportReason | '');
              setCursor(undefined);
            }}
            aria-label="Motivo"
          >
            <option value="">Todos los motivos</option>
            {(Object.keys(REASON_LABEL) as AiReportReason[]).map((r) => (
              <option key={r} value={r}>
                {REASON_LABEL[r]}
              </option>
            ))}
          </select>
          <select
            value={surface}
            onChange={(e) => {
              setSurface(e.target.value as AiReportSurface | '');
              setCursor(undefined);
            }}
            aria-label="Dónde"
          >
            <option value="">Todas las pantallas</option>
            {(Object.keys(SURFACE_LABEL) as AiReportSurface[]).map((s) => (
              <option key={s} value={s}>
                {SURFACE_LABEL[s]}
              </option>
            ))}
          </select>
        </div>
      </div>
      {query.isPending ? (
        <div className="loading">Cargando…</div>
      ) : query.isError ? (
        <div className="loading">Error: {query.error.message}</div>
      ) : (
        <section className="card">
          <table className="data-table">
            <thead>
              <tr>
                <th>Cuándo</th>
                <th>Alumno</th>
                <th>Motivo</th>
                <th>Dónde</th>
                <th>Respuesta reportada</th>
                <th>Comentario</th>
              </tr>
            </thead>
            <tbody>
              {query.data.reports.length === 0 ? (
                <tr>
                  <td colSpan={6} className="empty">
                    Sin reportes
                  </td>
                </tr>
              ) : (
                query.data.reports.map((r) => <ReportRow key={r.id} report={r} />)
              )}
            </tbody>
          </table>
          {query.data.nextCursor && (
            <div className="pager">
              <button
                type="button"
                className="btn-secondary"
                onClick={() => setCursor(query.data.nextCursor)}
              >
                Siguiente página
              </button>
            </div>
          )}
        </section>
      )}
    </>
  );
}

function ReportRow({ report }: { report: AiContentReport }): ReactElement {
  return (
    <tr>
      <td>{formatDateTime(report.createdAt)}</td>
      <td>
        <Link to={`/users/${report.userId}`}>{report.email}</Link>
      </td>
      <td>{REASON_LABEL[report.reason]}</td>
      <td>
        {SURFACE_LABEL[report.surface]}
        {report.sessionId && (
          <>
            {' · '}
            <Link to={`/sessions/${report.sessionId}`}>
              sesión{report.turnIndex != null ? ` · turno ${report.turnIndex}` : ''}
            </Link>
          </>
        )}
      </td>
      <td style={{ whiteSpace: 'pre-wrap', maxWidth: 480 }}>{report.text}</td>
      <td style={{ whiteSpace: 'pre-wrap' }}>{report.comment ?? '—'}</td>
    </tr>
  );
}
